import { useCMSPageField } from '@/cms/fields'
import { useBookingNumber, useGetGroupStatus, useTransformBookingToCheckoutQuery } from '@/domain/groups/hooks'
import { groupBookingsAPIClient } from '@/infrastructure/groups/api'
import { Custom500 } from '@/pages/500'
import { Basket } from '@/scopes/checkout/components/Basket'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError'
import {
  CheckoutHeading,
  CheckoutHeadingDescription,
  CheckoutHeadingTitle,
} from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { Services } from '@/scopes/checkout/components/services'
import { CheckoutContextProvider } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import {
  Content,
  Loader,
  MainContent,
  PageLayout,
  SideBarContent,
  Wrapper,
} from '@/scopes/checkout/groups/components/Layout'
import StatusAlert from '@/scopes/checkout/groups/components/StatusAlert'
import { StatusNotification } from '@/scopes/checkout/groups/components/StatusNotification'
import { GroupsDependenciesProvider } from '@/scopes/checkout/groups/dependencies/context'
import { checkoutAPIClient } from '@/scopes/checkout/infrastructure/api'
import { Hero } from '@/scopes/checkout/pages/groups/Components/Hero'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import createNextRouter from '@/scopes/common/infrastructure/router/nextRouter'
import { SpaceBetween, tk } from '@sunwebgroup/ui-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { Header } from '../../groups/components/Header'

const queryClient = new QueryClient()

function PageHeading() {
  const intl = useIntl()
  const { data } = useBookingDraftQuery()

  return (
    <CheckoutHeading direction="y">
      <CheckoutHeadingTitle>
        {intl.formatMessage(
          { id: 'groups_checkout_steps_extra_services_title' },
          { group_name: data?.groupInformation?.name ?? '' },
        )}
      </CheckoutHeadingTitle>
      <CheckoutHeadingDescription>
        {intl.formatMessage({ id: 'groups_checkout_steps_extra_services_description' })}
      </CheckoutHeadingDescription>
    </CheckoutHeading>
  )
}

function Page() {
  const sid = useCheckoutSid()
  const { data } = useBookingDraftQuery()
  const groupsHeader = useCMSPageField('Groups.header') ?? null

  if (!sid) {
    return null
  }

  return (
    <>
      {data?.error?.hasError && <CheckoutError showRedirectButton />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <>
          {groupsHeader && <Hero /> }
          <Wrapper>
            <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
              {!groupsHeader && <CheckoutProgress />}
              <Content>
                <MainContent>
                  {!groupsHeader && <PageHeading /> }
                  <Services />
                  <CheckoutNextStep />
                </MainContent>
                <SideBarContent direction="y" spacing={tk('spacing_lg')}>
                  <Basket />
                </SideBarContent>
              </Content>
            </SpaceBetween>
          </Wrapper>
        </>
      )}
    </>
  )
}

function WrapperContent() {
  const bookingNumber = useBookingNumber()
  const sid = useCheckoutSid()
  const { isLoading: getStatusIsLoading, isFetched: getStatusIsFetched, data: getStatusData } = useGetGroupStatus()
  const {
    isLoading: transformIsLoading,
    isFetched: transformIsFetched,
    data: transformBookingData,
  } = useTransformBookingToCheckoutQuery()

  const { isLoading, isFetching } = useBookingDraftQuery()

  const [isPageLoading, setIsPageLoading] = React.useState(true)

  React.useEffect(() => {
    if (sid) {
      setIsPageLoading(false)
    }
    if (getStatusIsFetched && !getStatusData?.isValid) {
      setIsPageLoading(false)
    }
    if (
      transformIsFetched
      && getStatusIsFetched
      && !isFetching
      && !(isLoading && getStatusIsLoading && transformIsLoading)
    ) {
      setIsPageLoading(false)
    }
  }, [
    getStatusData,
    sid,
    transformIsFetched,
    getStatusIsFetched,
    isFetching,
    isLoading,
    transformIsLoading,
    getStatusIsLoading,
  ])

  return (
    <>
      {isPageLoading
        ? (
            <Loader />
          )
        : (
            <>
              {bookingNumber && !sid && <StatusAlert />}
              {transformBookingData && transformBookingData.error?.hasError && (
                <StatusNotification status="error" groupName={getStatusData?.groupName} />
              )}
              <Page />
            </>
          )}
    </>
  )
}

export default function CheckoutPageStep1() {
  const router = useRouter()
  const contextItemId = useCMSPageField('contextItemId')

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutContextProvider
        value={{
          contextItemId,
        }}
      >
        <GroupsDependenciesProvider
          dependencies={{ checkoutAPIClient, groupBookingsAPIClient, router: createNextRouter(router) }}
        >
          <PageLayout>
            <Header hideLiveSearch />
            <NewRelicErrorBoundary scope="groups-page" fallback={<Custom500 brand="sunweb" />}>
              <WrapperContent />
            </NewRelicErrorBoundary>
          </PageLayout>
          <DevTools />
        </GroupsDependenciesProvider>
      </CheckoutContextProvider>
    </QueryClientProvider>
  )
}
